import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookMarked, ArrowRight, User, GraduationCap, Download, Timer, Trophy, FileText } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/Section";
import SubjectIcon from "@/components/SubjectIcon";
import KitCard from "@/components/KitCard";
import AuthGateModal from "@/components/AuthGateModal";
import { useKitAccess } from "@/lib/useKitAccess";

export default function Dashboard() {
  const { user } = useAuth();
  const [kits, setKits] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingNext, setPendingNext] = useState(null);
  const openKit = useKitAccess(setGateOpen, setPendingNext);

  useEffect(() => {
    api.get("/kits?limit=6").then(({ data }) => setKits(data)).catch(() => {});
    api.get("/subjects").then(({ data }) => setSubjects(data)).catch(() => {});
    api.get("/me/downloads").then(({ data }) => setDownloads(data)).catch(() => {});
    api.get("/me/attempts").then(({ data }) => setAttempts(data)).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="rounded-sm bg-[#0B2545] p-6 text-white" data-testid="dashboard-welcome">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FF9933] font-serif text-lg font-bold text-slate-900">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm text-slate-300">Welcome back,</p>
            <h1 className="font-serif text-xl font-bold md:text-2xl">{user?.name}</h1>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          {user?.target_year && <span className="rounded-sm bg-white/10 px-3 py-1">🎯 Target: CSE {user.target_year}</span>}
          {user?.state && <span className="rounded-sm bg-white/10 px-3 py-1">📍 {user.state}</span>}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Link to="/revision-kits" className="gov-card flex items-center gap-3 p-4 hover:shadow-md" data-testid="dash-kits">
          <BookMarked size={26} className="text-[#1F3A68]" />
          <div><div className="font-serif font-bold text-[#0B2545]">Browse All Kits</div><div className="text-xs text-gray-500">Free subject-wise library</div></div>
        </Link>
        <Link to="/courses" className="gov-card flex items-center gap-3 p-4 hover:shadow-md" data-testid="dash-courses">
          <GraduationCap size={26} className="text-[#1F3A68]" />
          <div><div className="font-serif font-bold text-[#0B2545]">Courses</div><div className="text-xs text-gray-500">Mentor-led programmes</div></div>
        </Link>
        <Link to="/contact" className="gov-card flex items-center gap-3 p-4 hover:shadow-md" data-testid="dash-contact">
          <User size={26} className="text-[#1F3A68]" />
          <div><div className="font-serif font-bold text-[#0B2545]">Need Help?</div><div className="text-xs text-gray-500">Get in touch</div></div>
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="gov-card overflow-hidden" data-testid="my-downloads">
          <div className="gov-card-header flex items-center gap-2 px-4 py-2.5 font-serif text-base font-bold"><Download size={16} /> My Downloads</div>
          {downloads.length === 0 ? (
            <p className="p-5 text-sm text-gray-500">You haven't downloaded any kits yet. <Link to="/revision-kits" className="prose-link">Browse the library</Link>.</p>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]">
              {downloads.slice(0, 6).map((d) => (
                <li key={d.kit_id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`download-${d.kit_id}`}>
                  <FileText size={16} className="shrink-0 text-[#1F3A68]" />
                  <span className="flex-1 truncate text-[#0B2545]">{d.kit_title}</span>
                  <span className="shrink-0 text-xs text-gray-400">{new Date(d.downloaded_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="gov-card overflow-hidden" data-testid="my-attempts">
          <div className="gov-card-header flex items-center justify-between px-4 py-2.5 font-serif text-base font-bold">
            <span className="flex items-center gap-2"><Trophy size={16} /> My Test Attempts</span>
            <Link to="/mock-tests" className="text-xs font-normal text-white/90 hover:underline">Take a test</Link>
          </div>
          {attempts.length === 0 ? (
            <p className="p-5 text-sm text-gray-500">No attempts yet. <Link to="/mock-tests" className="prose-link">Try a Prelims mock</Link>.</p>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]">
              {attempts.slice(0, 6).map((a) => (
                <li key={a.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`attempt-${a.id}`}>
                  <Timer size={15} className="shrink-0 text-[#1F3A68]" />
                  <span className="flex-1 truncate text-[#0B2545]">{a.test_title}</span>
                  <span className={`shrink-0 rounded-sm px-2 py-0.5 text-xs font-bold ${a.score >= a.max_marks * 0.5 ? "bg-[#16A34A]/10 text-[#16A34A]" : "bg-gray-100 text-gray-600"}`}>{a.score}/{a.max_marks}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8">
        <SectionTitle sub="Jump straight into a subject" testId="dash-subjects-title">Subjects</SectionTitle>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {subjects.map((s) => (
            <Link key={s.id} to={`/revision-kits?subject=${s.slug}`} className="gov-card flex items-center gap-2 p-3 hover:shadow-md" data-testid={`dash-subject-${s.slug}`}>
              <SubjectIcon name={s.icon} size={18} />
              <span className="truncate text-sm font-semibold text-[#0B2545]">{s.name}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-end justify-between">
          <SectionTitle sub="Freshly added to the library" testId="dash-latest-title">Latest Kits</SectionTitle>
          <Link to="/revision-kits" className="mb-6 flex items-center gap-1 text-sm font-semibold text-[#0056B3] hover:underline">View all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {kits.map((k) => <KitCard key={k.id} kit={k} onOpen={openKit} />)}
        </div>
      </div>

      <AuthGateModal open={gateOpen} onOpenChange={setGateOpen} nextPath={pendingNext} />
    </div>
  );
}
